
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Database } from "@/integrations/supabase/types";
import { sendRecommendationsAvailableNotification } from "@/services/notification";

type EventStatus = Database["public"]["Enums"]["event_status"];
type TimeOfDay = Database["public"]["Enums"]["time_of_day"];

interface ConfirmSelectionParams {
  dateId: string;
  venueId: string;
  timePreference?: string;
}

export const useRecommendationModal = (eventId: string) => {
  const [selectedTimePreference, setSelectedTimePreference] = useState<string | undefined>(undefined);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: guests, isLoading: isLoadingGuests } = useQuery({
    queryKey: ['guests', eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_invites')
        .select(`
          invite_id,
          display_name,
          phone_number,
          rsvp_status,
          selected_dates,
          guest_avatar_url,
          claimed_by_user_id,
          user:claimed_by_user_id (
            avatar_url
          )
        `)
        .eq('event_id', eventId)
        .order('display_name', { ascending: true });

      if (error) throw error;
      
      // Process the data to have a consistent structure
      return data?.map(guest => ({
        invite_id: guest.invite_id,
        display_name: guest.display_name,
        rsvp_status: guest.rsvp_status,
        selected_dates: guest.selected_dates || [],
        // Use guest_avatar_url as the primary avatar, fallback to user's avatar_url
        avatar_url: guest.guest_avatar_url || guest.user?.avatar_url,
        guest_avatar_url: guest.guest_avatar_url,
      })) || [];
    }
  });

  // Fetch event details to get the host ID
  const { data: eventDetails } = useQuery({
    queryKey: ['event-host-details', eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('events')
        .select('host_user_id')
        .eq('event_id', eventId)
        .single();

      if (error) throw error;
      return data;
    }
  });

  // Mutation for sending notification
  const sendNotificationMutation = useMutation({
    mutationFn: async (hostId: string) => {
      const result = await sendRecommendationsAvailableNotification(eventId, hostId);
      return result;
    },
    onError: (error) => {
      console.error('Error sending recommendations notification:', error);
      // We don't show an error toast here as it's a background operation
    }
  });

  const confirmSelections = useMutation({
    mutationFn: async (params: ConfirmSelectionParams) => {
      const { dateId, venueId, timePreference } = params;
      
      // Mark the selected date as selected
      const { error: dateError } = await supabase
        .from('event_date_recommendations')
        .update({ is_selected: true, selected_at: new Date().toISOString() })
        .eq('recommendation_id', dateId);

      if (dateError) throw dateError;

      // Mark the selected venue as selected
      const { error: venueError } = await supabase
        .from('event_venue_recommendations')
        .update({ is_selected: true, selected_at: new Date().toISOString() })
        .eq('recommendation_id', venueId);

      if (venueError) throw venueError;

      // Update event status to confirmed
      const { error: eventError } = await supabase
        .from('events')
        .update({ 
          status: 'confirmed' as EventStatus,
          specific_time_preference: timePreference  // Storing the human-readable label
        })
        .eq('event_id', eventId);

      if (eventError) throw eventError;

      // Record the selection in the history table
      const { error: historyError } = await supabase
        .from('event_selection_history')
        .insert({
          event_id: eventId,
          date_recommendation_id: dateId,
          venue_recommendation_id: venueId
        });

      if (historyError) throw historyError;

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      
      // Send notification to the host after successful confirmation
      if (eventDetails?.host_user_id) {
        sendNotificationMutation.mutate(eventDetails.host_user_id);
      } else {
        console.error('Could not find host_user_id for event:', eventId);
      }
      
      toast({
        title: "Success",
        description: "Selections confirmed successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to confirm selections",
        variant: "destructive",
      });
      console.error('Error confirming selections:', error);
    }
  });

  const updateTimePreference = (preference: string) => {
    setSelectedTimePreference(preference);
    toast({
      title: "Success",
      description: "Time preference updated successfully",
    });
  };

  return {
    confirmSelections: (dateId: string, venueId: string, timePreference?: string) => 
      confirmSelections.mutate({ dateId, venueId, timePreference }),
    isSubmitting: confirmSelections.isPending,
    guests,
    isLoadingGuests,
    updateTimePreference,
    selectedTimePreference
  };
};
